<template>
  <div class="data-table">
    <!-- Search row -->
    <div class="row mb-4" v-if="showSearch">
      <div class="col-md-12">
        <label>{{ searchLabel }}</label>
        <input class="form-control" v-model="searchTerm" :placeholder="searchPlaceholder">
      </div>
    </div>
    
    <!-- Table -->
    <div class="table-responsive">
      <table class="table table-flush">
        <thead class="thead-light">
          <tr>
            <th v-for="column in columns" :key="column.key" 
                :class="getColumnHeaderClass(column)">
              {{ column.label }}
            </th>
          </tr>
        </thead>
        <tbody>
          <template v-if="!loading && items.length > 0">
            <tr v-for="(item, index) in items" :key="index"
                :class="item._rowClass || ''">
              <td v-for="column in columns" :key="column.key" 
                  :class="getColumnClass(column)">
                <slot :name="column.key" :item="item" :index="index">
                  <template v-if="column.formatter">
                    <!-- eslint-disable-next-line -->
                    <span v-html="column.formatter(getItemValue(item, column.key))"></span>
                  </template>
                  <template v-else>
                    {{ getItemValue(item, column.key) }}
                  </template>
                </slot>
              </td>
            </tr>
          </template>
          
          <tr v-if="!loading && items.length === 0">
            <td :colspan="columns.length" class="text-center">
              <p>{{ emptyMessage }}</p>
            </td>
          </tr>
          
          <tr v-if="loading">
            <td :colspan="columns.length" class="text-center">
              <div class="spinner-border text-primary" role="status">
                <span class="visually-hidden">Carregando...</span>
              </div>
              <p class="mt-2">Carregando...</p>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
    
    <!-- Pagination -->
    <div class="d-flex justify-content-between align-items-center mt-4" v-if="pagination && pagination.totalPages > 1">
      <div class="pagination-info">
        Mostrando {{ startRecord }} - {{ endRecord }} de {{ pagination.total }} registros
      </div>
      <nav aria-label="Navegação de página">
        <ul class="pagination mb-0">
          <!-- Primeira página -->
          <li class="page-item" :class="{ disabled: !pagination.hasPreviousPage }">
            <a class="page-link" href="#" aria-label="Primeira" @click.prevent="onPageChange(1)">
              <span aria-hidden="true">&laquo;&laquo;</span>
            </a>
          </li>
          <!-- Página anterior -->
          <li class="page-item" :class="{ disabled: !pagination.hasPreviousPage }">
            <a class="page-link" href="#" aria-label="Anterior" @click.prevent="onPageChange(pagination.currentPage - 1)">
              <span aria-hidden="true">&laquo;</span>
            </a>
          </li>
          <!-- Números das páginas -->
          <li class="page-item" v-for="page in pageNumbers" :key="page" 
              :class="{ active: page === pagination.currentPage }">
            <a class="page-link" href="#" @click.prevent="onPageChange(page)">{{ page }}</a>
          </li>
          <!-- Próxima página -->
          <li class="page-item" :class="{ disabled: !pagination.hasNextPage }">
            <a class="page-link" href="#" aria-label="Próximo" @click.prevent="onPageChange(pagination.currentPage + 1)">
              <span aria-hidden="true">&raquo;</span>
            </a>
          </li>
          <!-- Última página -->
          <li class="page-item" :class="{ disabled: !pagination.hasNextPage }">
            <a class="page-link" href="#" aria-label="Última" @click.prevent="onPageChange(pagination.totalPages)">
              <span aria-hidden="true">&raquo;&raquo;</span>
            </a>
          </li>
        </ul>
      </nav>
    </div>
  </div>
</template>

<script>
import { getPageNumbers } from '@/utils/pagination';

export default {
  name: 'DataTable',
  emits: ['search', 'page-change'],
  props: {
    // Table data
    items: {
      type: Array,
      required: true
    },
    // Column definitions [{ key, label, formatter, class }]
    columns: {
      type: Array,
      required: true
    },
    // Pagination data
    pagination: {
      type: Object,
      default: null
    },
    // Loading state
    loading: {
      type: Boolean,
      default: false
    },
    // Search options
    showSearch: {
      type: Boolean,
      default: true
    },
    searchValue: {
      type: String,
      default: ''
    },
    searchLabel: {
      type: String,
      default: 'Pesquisar'
    },
    searchPlaceholder: {
      type: String,
      default: 'Digite para pesquisar...'
    },
    // Empty message
    emptyMessage: {
      type: String,
      default: 'Nenhum registro encontrado'
    },
    // Row class (function or string)
    rowClass: {
      type: [Function, String],
      default: null
    }
  },
  data() {
    return {
      searchTerm: this.searchValue
    };
  },
  computed: {
    pageNumbers() {
      if (!this.pagination) return [];
      return getPageNumbers(this.pagination.currentPage, this.pagination.totalPages);
    },
    startRecord() {
      if (!this.pagination) return 0;
      return (this.pagination.currentPage - 1) * this.pagination.pageSize + 1;
    },
    endRecord() {
      if (!this.pagination) return 0;
      const end = this.pagination.currentPage * this.pagination.pageSize;
      return end > this.pagination.total ? this.pagination.total : end;
    }
  },
  watch: {
    searchValue(newVal) {
      this.searchTerm = newVal;
    },
    searchTerm(newVal) {
      this.$emit('search', newVal);
    }
  },
  methods: {
    onPageChange(page) {
      this.$emit('page-change', page);
    },
    getItemValue(item, key) {
      if (!key.includes('.')) return item[key];
      
      // Handle nested properties like "user.name"
      return key.split('.').reduce((obj, k) => (obj && obj[k] !== undefined) ? obj[k] : null, item);
    },
    getColumnHeaderClass(column) {
      const baseClass = 'text-uppercase text-secondary text-xxs font-weight-bolder opacity-7';
      return column.headerClass ? `${baseClass} ${column.headerClass}` : baseClass;
    },
    getColumnClass(column) {
      const baseClass = 'text-xs font-weight-bold';
      return column.class ? `${baseClass} ${column.class}` : baseClass;
    },
    getRowClassName(item) {
      if (!this.rowClass) return '';
      
      if (typeof this.rowClass === 'function') {
        return this.rowClass(item);
      }
      
      return this.rowClass;
    }
  }
};
</script>

<style scoped>
.pagination {
  cursor: pointer;
  margin-bottom: 0;
}

.pagination-info {
  font-size: 0.875rem;
  color: #8392ab;
}

.data-table .table th {
  padding: 0.35rem 0.5rem;
  text-transform: uppercase;
  letter-spacing: 0;
  border-bottom: 1px solid #e9ecef;
}

.data-table .table td {
  padding: 0.25rem 0.5rem;
  vertical-align: middle;
  border-bottom: 1px solid #e9ecef;
}

.data-table .table tr.empresa-selecionada td {
  background-color: rgba(46, 184, 92, 0.3) !important;
}

.data-table .table tr.empresa-selecionada {
  position: relative;
}

.data-table .table tr.empresa-selecionada td:first-child {
  position: relative;
  padding-left: 1.2rem;
}

.data-table .table tr.empresa-selecionada td:first-child::before {
  content: "✓";
  color: #2eb85c;
  font-weight: bold;
  position: absolute;
  left: 0.4rem;
  top: 50%;
  transform: translateY(-50%);
}
</style> 